// ============================================================================
// MIRRAY THEME HOOK
// ============================================================================

import { useEffect } from 'react';
import { useUIStore } from '../stores/uiStore';

// ============================================================================
// THEME TYPES
// ============================================================================

export type Theme = 'monochrome' | 'monochrome-dark';

// ============================================================================
// THEME HOOK
// ============================================================================

/**
 * Hook for managing theme state and applying themes to the document
 * Handles system theme detection and data-theme attribute management
 */
export function useTheme() {
  const { theme, setTheme } = useUIStore();

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return {
    theme,
    setTheme,
  };
}

// ============================================================================
// THEME UTILITIES
// ============================================================================

/**
 * Get the effective theme (resolves 'system' to actual theme)
 */
export function getEffectiveTheme(): Theme {
  if (typeof window === 'undefined') return 'monochrome';
  const dataTheme = document.documentElement.getAttribute('data-theme');
  return (dataTheme === 'monochrome-dark') ? 'monochrome-dark' : 'monochrome';
}

/**
 * Check if current theme is dark
 */
export function isDarkTheme(): boolean {
  return getEffectiveTheme() === 'monochrome-dark';
}

/**
 * Check if current theme is monochrome (always true — only monochrome themes exist)
 */
export function isMonochromeTheme(): boolean {
  return true;
}

// ============================================================================
// EXPORTS
// ============================================================================

export default useTheme;
